<template>
  <TheMainContent>
    <RevLoadingScreen v-if="isLoading" text="loading" />

    <component
      :is="paymentResultComponent"
      v-if="!isLoading"
      class="bg-static-default-mid pb-80 md:pb-72"
      :data-qa="`payment-result-${paymentStatus}`"
      :payment="paymentData"
    />

    <Trustmark />
  </TheMainContent>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, onMounted, ref } from 'vue'

import { getResult } from '@backmarket/http-api/src/api-specs-payment/payment/payment'
import type { PaymentResult } from '@backmarket/http-api/src/api-specs-payment/payment/payment-result.types'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import Trustmark from '~/scopes/trustmark/Trustmark.vue'

import PaymentFailure from './components/payment-error/PaymentFailure/PaymentFailure.vue'
import BouyguesPaymentSuccess from './components/payment-success/BouyguesPaymentSuccess/BouyguesPaymentSuccess.vue'
import PaymentSuccess from './components/payment-success/PaymentSuccess/PaymentSuccess.vue'
import { usePaymentResultEventTracker } from './usePaymentResultEventTracker'

const userStore = useUserStore()
const route = useRoute()

const { paymentId } = route.params

const {
  data: payment,
  error: paymentError,
  pending: isLoading,
} = paymentId
  ? await useHttpFetch(getResult, {
      pathParams: { paymentId },
    })
  : { data: ref(), error: ref(), pending: false }

const paymentResult = ref(payment.value as PaymentResult)

const paymentResultComponent = computed(() => {
  if (!paymentId || paymentError.value) {
    return PaymentFailure
  }

  if (!paymentResult.value) {
    return null
  }
  const hasOnlineMobilePlan = paymentResult.value?.orders.some((order) =>
    order?.orderlines.some(
      (orderline) =>
        !isEmpty(orderline.mobilePlan?.subscriptionId) &&
        orderline.mobilePlan?.offer?.type === 'ONLINE',
    ),
  )

  if (paymentResult.value.success && hasOnlineMobilePlan) {
    return BouyguesPaymentSuccess
  }

  return paymentResult.value.success ? PaymentSuccess : PaymentFailure
})

const paymentStatus = computed(() => {
  if (!paymentResult.value) {
    return 'loading'
  }

  return paymentResult.value.success ? 'success' : 'failure'
})

const paymentData = computed(() => {
  return {
    ...payment.value,
    errorMessage: payment.value?.errorMessage || paymentError.value?.message,
  }
})

onMounted(async () => {
  if (!paymentId || paymentError.value) {
    return
  }

  const isSuccess = paymentResult.value.success
  const track = usePaymentResultEventTracker(paymentResult.value)

  if (isSuccess) {
    track.paymentSuccess()
  } else {
    track.paymentFailure()
  }

  try {
    await userStore.fetchUser()
  } catch {
    // Empty catch until we tackle the following issue: CK-3553
  }
})
</script>
